/**
 * Theme global JS — vanilla ES2019+, no dependencies, no build step.
 *
 * Provides:
 * - Mobile nav drawer (open/close, overlay, Escape, focus trap)
 * - Mega menu open on hover/focus-within, close on mouseleave/Escape
 * - A reusable accordion controller ([data-accordion-toggle])
 * - Announcement bar message rotation
 * - A tiny cart helper (window.cart.add) that keeps [data-cart-count] in sync
 *
 * Every query is guarded so this file never throws when a section/element
 * is absent from the page.
 */
(function () {
  'use strict';

  /* ─── Helpers ──────────────────────────────────────────────────────── */

  function getFocusable(container) {
    if (!container) return [];
    var nodes = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.slice.call(nodes).filter(function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  function trapFocusHandler(container) {
    return function (event) {
      if (event.key !== 'Tab') return;
      var focusable = getFocusable(container);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
  }

  /* ─── Mobile drawer ────────────────────────────────────────────────── */

  function initDrawers(root) {
    var scope = root || document;
    var toggles = scope.querySelectorAll('[data-drawer-toggle]');
    if (!toggles.length) return;

    toggles.forEach(function (toggle) {
      if (toggle.dataset.drawerBound) return;
      toggle.dataset.drawerBound = 'true';

      var panelId = toggle.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;

      var shell = panel.closest('[data-drawer-shell]');
      if (!shell) return;

      var overlay = shell.querySelector('[data-drawer-overlay]');
      var closeButtons = shell.querySelectorAll('[data-drawer-close]');
      var trapHandler = null;

      function open() {
        shell.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        trapHandler = trapFocusHandler(panel);
        panel.addEventListener('keydown', trapHandler);
        var focusable = getFocusable(panel);
        if (focusable.length) focusable[0].focus();
      }

      function close() {
        shell.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        if (trapHandler) {
          panel.removeEventListener('keydown', trapHandler);
          trapHandler = null;
        }
        toggle.focus();
      }

      toggle.addEventListener('click', function () {
        if (shell.classList.contains('is-open')) {
          close();
        } else {
          open();
        }
      });

      if (overlay) overlay.addEventListener('click', close);
      closeButtons.forEach(function (btn) {
        btn.addEventListener('click', close);
      });

      shell.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && shell.classList.contains('is-open')) {
          close();
        }
      });
    });
  }

  /* ─── Mega menu ────────────────────────────────────────────────────── */

  function initMegaMenus(root) {
    var scope = root || document;
    var items = scope.querySelectorAll('[data-mega-menu]');
    if (!items.length) return;

    items.forEach(function (item) {
      if (item.dataset.megaMenuBound) return;
      item.dataset.megaMenuBound = 'true';

      var link = item.querySelector('.header__nav-link');

      function openMenu() {
        item.classList.add('is-open');
      }

      function closeMenu() {
        item.classList.remove('is-open');
      }

      item.addEventListener('mouseenter', openMenu);
      item.addEventListener('mouseleave', closeMenu);
      item.addEventListener('focusin', openMenu);
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) closeMenu();
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          closeMenu();
          if (link) link.focus();
        }
      });
    });
  }

  /* ─── Accordion controller (reused by drawer + other sections) ──────── */

  function initAccordions(root) {
    var scope = root || document;
    var toggles = scope.querySelectorAll('[data-accordion-toggle]');
    if (!toggles.length) return;

    toggles.forEach(function (toggle) {
      if (toggle.dataset.accordionBound) return;
      toggle.dataset.accordionBound = 'true';

      toggle.addEventListener('click', function () {
        var panelId = toggle.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        var expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        panel.hidden = expanded;
      });
    });
  }

  /* ─── Announcement bar rotation ──────────────────────────────────────── */

  function initAnnouncementBar(root) {
    var scope = root || document;
    var bar = scope.querySelector('[data-announcement-autorotate]');
    if (!bar) return;

    var messages = bar.querySelectorAll('[data-announcement-message]');
    if (messages.length < 2) return;

    var speed = parseFloat(bar.getAttribute('data-announcement-speed')) || 5;
    var index = 0;

    if (bar.dataset.announcementBound) return;
    bar.dataset.announcementBound = 'true';

    setInterval(function () {
      messages[index].hidden = true;
      index = (index + 1) % messages.length;
      messages[index].hidden = false;
    }, speed * 1000);
  }

  /* ─── Cart helper ─────────────────────────────────────────────────────── */

  function updateCartCount(count) {
    var elements = document.querySelectorAll('[data-cart-count]');
    elements.forEach(function (el) {
      el.textContent = count;
      if (count > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', 'hidden');
      }
    });
  }

  function fetchCart() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  window.cart = window.cart || {};

  window.cart.add = function (id, quantity) {
    if (!window.routes || !window.routes.cart_add_url) {
      return Promise.reject(new Error('window.routes.cart_add_url is not defined'));
    }

    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: id, quantity: quantity || 1 })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        return fetchCart().then(function (cartData) {
          updateCartCount(cartData.item_count);
          return data;
        });
      });
  };

  window.cart.refresh = function () {
    return fetchCart().then(function (cartData) {
      updateCartCount(cartData.item_count);
      return cartData;
    });
  };

  /* ─── Init ────────────────────────────────────────────────────────────── */

  function initAll(root) {
    initDrawers(root);
    initMegaMenus(root);
    initAccordions(root);
    initAnnouncementBar(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Re-bind newly injected markup when the theme editor swaps section HTML.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
